import {
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IBreadcrumbPageProps from "./type";
import type {
    BreadcrumbPageNativePropsType,
    BreadcrumbPageWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useBreadcrumbContext
} from "../../context";
import {
    getBreadcrumbMeasures
} from "../../stylesheet";
import Skeleton from "../../../skeleton";
import Text from "../../../text";

const BreadcrumbPage: FC<IBreadcrumbPageProps> = ({
    customTheme: customThemeProp,
    icon: IconComponent,
    isLoading = false,
    style,
    title,
    ...props
}) => {
    const context = useBreadcrumbContext();

    const customTheme = customThemeProp ?? context.customTheme;

    const {
        shapeConfigs,
        typography,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const measures = getBreadcrumbMeasures({
        size: context.size,
        typography,
        iconSizes,
        spaces
    });

    const {
        container: containerDynamicStyle,
        title: titleDynamicStyle
    } = useStyles({
        measures,
        spaces
    });

    const webProps: BreadcrumbPageWebPropsType = Platform.OS === "web" ? {
        "aria-current": "page"
    } : {};

    const nativeProps: BreadcrumbPageNativePropsType = Platform.OS === "web" ? {} : {
        accessibilityState: {
            selected: true
        }
    };

    const renderContent = () => {
        if(isLoading) {
            return <Skeleton
                width={shapeConfigs.sideListWidth / 2}
                typography={measures.pageVariant}
                customTheme={customTheme}
                variant="text"
            />;
        }

        return <>
            {
                IconComponent ?
                    <IconComponent
                        customColor={colors.content.icon.high}
                        size={measures.iconSize}
                        color="high"
                    />
                :
                    null
            }
            <Text
                customColor={colors.content.text.high}
                variant={measures.pageVariant}
                customTheme={customTheme}
                style={[
                    stylesheet.title,
                    titleDynamicStyle
                ]}
                numberOfLines={1}
            >
                {title}
            </Text>
        </>;
    };

    return <View
        {...props}
        {...webProps}
        {...nativeProps}
        aria-label={isLoading ? localize("loading") : undefined}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-busy={isLoading}
    >
        {renderContent()}
    </View>;
};
export default BreadcrumbPage;
